import { clamp } from '@/number';

export type ColorRGB = {
  r: number;
  g: number;
  b: number;
};

export type ColorHSL = {
  h: number;
  s: number;
  l: number;
};

export function isHexColor(input: string): boolean {
  if (input.startsWith('#')) {
    input = input.substring(1);
  }

  if (input.length !== 3 && input.length !== 6) {
    return false;
  }

  return !isNaN(parseInt(input, 16));
}

export function hexToRgb(hex: string): ColorRGB {
  if (hex.startsWith('#')) {
    hex = hex.substring(1);
  }

  if (!isHexColor(hex)) {
    throw new Error(`Failed to parse hex color '${hex}.'`);
  }

  let r;
  let g;
  let b;

  if (hex.length === 3) {
    r = parseInt(hex.charAt(0).repeat(2), 16);
    g = parseInt(hex.charAt(1).repeat(2), 16);
    b = parseInt(hex.charAt(2).repeat(2), 16);
  } else {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }

  return { r, g, b };
}

export function rgbToHex(color: ColorRGB): string {
  const hex = ((1 << 24) + (color.r << 16) + (color.g << 8) + color.b)
    .toString(16)
    .slice(1);

  return `#${hex}`;
}

export function rgbToHsl(color: ColorRGB): ColorHSL {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;

    if (l > 0.5) {
      s = d / (2 - max - min);
    } else {
      s = d / (max + min);
    }

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: clamp(h, 0, 1),
    s: clamp(s, 0, 1),
    l: clamp(l, 0, 1),
  };
}

export function hslToRgb(color: ColorHSL): ColorRGB {
  const { h, s, l } = color;
  let r = l;
  let g = l;
  let b = l;

  if (color.s !== 0) {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: clamp(Math.round(r * 255), 0, 255),
    g: clamp(Math.round(g * 255), 0, 255),
    b: clamp(Math.round(b * 255), 0, 255),
  };
}

export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  hsl.l = clamp(hsl.l * (1 + percent), 0, 1);
  return rgbToHex(hslToRgb(hsl));
}
